import {
  adminResources,
  ControlClientError,
  parseContextSession,
  type ControlWebClient,
  type ContextSessionResponse,
  type RequestOptions,
  type IssueOrganizationInvitationRequest,
  type OrganizationInvitationResponse,
} from "@kontextwerk/control-web-client"
import {
  invitationEnvelope,
  invitationPage,
  timestamp,
  uuid,
  type InvitationPage,
} from "./_logic/contracts"

export type { InvitationPage } from "./_logic/contracts"

type Capture = Required<Pick<RequestOptions, "signal" | "isCurrent" | "mutationContext">>

const MAX_INVITATION_PAGES = 20

/** A server without the invitation listing answers 404 or 405; that is no failure. */
const listingUnavailable = (error: unknown) =>
  error instanceof ControlClientError && [404, 405].includes(error.statusCodeOrStatus ?? 0)

export const createMembershipInvitationsApi = (
  client: ControlWebClient,
  value: ContextSessionResponse,
  capture: Capture,
) => {
  const session = parseContextSession(value)

  if (
    session.product_issuer !== "kehrblatt-control" ||
    session.context.kind !== "organization" ||
    session.session_id !== capture.mutationContext.expected_session_id ||
    session.context_revision !== capture.mutationContext.expected_context_revision
  )
    throw new TypeError("Unconfirmed invitation context")

  const org = session.context.organization_id
  const path = `/admin/organizations/${uuid(org)}/invitations`
  const resource = new adminResources.AdminIdentityAdministrationAPI(client.transport)
  const confirmed = (invitation: OrganizationInvitationResponse) => {
    if (invitation.product_issuer !== session.product_issuer)
      throw new TypeError("Unexpected invitation issuer")

    if (invitation.organization_id !== uuid(org))
      throw new TypeError("Unexpected invitation organization")

    return invitation
  }
  const options = {
    signal: capture.signal,
    isCurrent: capture.isCurrent,
    mutationContext: capture.mutationContext,
    retry: "never" as const,
  }
  /** Pending, unexpired invitations; null while the server does not offer the listing yet. */
  const list = async (after: string | null): Promise<InvitationPage | null> => {
    let page: InvitationPage
    try {
      page = invitationPage(
        await client.transport.request<unknown>("GET", path, {
          ...options,
          params: { limit: 100, ...(after ? { after_id: uuid(after) } : {}) },
        }),
      )
    } catch (error) {
      if (listingUnavailable(error)) return null

      throw error
    }

    return Object.freeze({
      items: Object.freeze(
        page.items
          .map(confirmed)
          .filter(
            (invitation) =>
              invitation.status === "pending" && Date.parse(invitation.expires_at) > Date.now(),
          ),
      ),
      next_after_id: page.next_after_id,
    })
  }

  return {
    async choices() {
      return (await resource.membershipRoleChoices(org, capture)).items
    },
    async issue(payload: IssueOrganizationInvitationRequest) {
      return confirmed((await resource.issueInvitation(org, payload, capture)).invitation)
    },
    async revoke(id: string) {
      return confirmed((await resource.revokeInvitation(org, id, capture)).invitation)
    },
    list,
    /** All pending invitations, or null if the server has no listing endpoint yet. */
    async listAll(): Promise<readonly OrganizationInvitationResponse[] | null> {
      const items: OrganizationInvitationResponse[] = []
      const cursors = new Set<string>()
      let after: string | null = null
      for (let pageIndex = 0; pageIndex < MAX_INVITATION_PAGES; pageIndex += 1) {
        const page: InvitationPage | null = await list(after)

        if (!page) return null

        items.push(...page.items)
        after = page.next_after_id

        if (!after) return Object.freeze(items)

        if (cursors.has(after)) throw new TypeError("Invitation cursor did not advance")

        cursors.add(after)
      }

      throw new TypeError("Invitation listing exceeds the supported size")
    },
    /** Issues a new pending invitation for the same recipient and role; the old one is revoked. */
    async resend(id: string, expiresAt: string) {
      return confirmed(
        invitationEnvelope(
          await client.transport.request<unknown>("POST", `${path}/${uuid(id)}/resend`, {
            ...options,
            requireCsrf: true,
            json: { expires_at: timestamp(expiresAt) },
          }),
        ),
      )
    },
  }
}
export type MembershipInvitationsApi = ReturnType<typeof createMembershipInvitationsApi>
